<template>
    <div class="mb-1">
        <!-- Uploaded File -->
        <div
            v-if="uploadedFile"
            class="card d-inline-flex align-middle mr-0 mr-md-2"
            :class="{ 'card-sm': size === 'sm' }"
        >
            <!-- Image Preview  -->
            <template v-if="uploadedFile.content_type && uploadedFile.content_type.startsWith('image')">
                <img
                    :src="inMemoryFileUrl || uploadedFile.signed_url"
                    class="card-img-top cover rounded"
                    alt="File Uploaded"
                />
            </template>

            <!-- File Placeholder with Download Link -->
            <template v-else>
                <div class="text-overlay text-secondary mb-0 text-center">
                    <span
                        v-if="uploadedFile.content_type.startsWith('video')"
                        class="icon icon-xl icon-glyphicons-basic-film mr-2 rounded align-middle"
                    ></span>
                    <span
                        v-else-if="uploadedFile.content_type.startsWith('audio')"
                        class="icon icon-xl icon-glyphicons-basic-equalizer-bars mr-2 rounded align-middle"
                    ></span>
                    <span v-else class="icon icon-xl icon-glyphicons-basic-file mr-2 rounded align-middle"></span>
                    <a
                        class="font-weight-bold align-middle text-secondary"
                        :href="uploadedFile.signed_url_original"
                        target="_blank"
                        >{{ uploadedFile.name }}</a
                    >
                </div>
            </template>

            <div @click="removeFile()" v-show="replaceable" class="remove-file">
                <span class="icon icon-sm icon-glyphicons-basic-times"></span>
            </div>
        </div>

        <!-- Upload New -->
        <div
            v-if="!uploadedFile"
            class="card d-inline-flex align-middle file-upload-container"
            :class="{
                'file-upload-error': error,
                'file-upload-container-sm card-sm': size === 'sm',
            }"
        >
            <input id="upload-single" @change="addFile($event)" type="file" class="file-upload-input" />
            <div class="text-overlay upload-overlay text-secondary mb-0 text-center">
                <span class="icon icon-xl" :class="placeholderIconClass"></span>
                <p class="mb-1 mt-1">{{ placeholder }}</p>
                <p class="small text-secondary mb-0">({{ accept }})</p>
            </div>
        </div>

        <div v-show="isUploading" class="upload-progress pt-1 mt-2 mb-2" :class="{ 'upload-progress-sm': size === 'sm' }">
            <div class="progress rounded" style="height: 4px">
                <div
                    class="progress-bar rounded"
                    role="progressbar"
                    :style="uploadProgressAsStyle"
                    :aria-valuenow="uploadProgressPercent"
                    aria-valuemin="0"
                    aria-valuemax="100"
                ></div>
            </div>
        </div>

        <div v-show="isUploaded && showUploadStatus" class="tiny mb-2 mt-2">
            <span class="icon icon-glyphicons-basic-circle-check text-success icon-mr"></span>
            <span class="text-secondary">Uploaded</span>
        </div>

        <div v-show="sizeError">
            <p class="small text-danger mb-3">
                The maximum allowable size for {{ fileTypeLabelPlural || "this media type" }} is {{ maxBytesReadable }},
                but your file is <strong>{{ fileSizeReadable }}</strong
                >.
                <span v-show="maxBytesMessage" class="mr-1">{{ maxBytesMessage }}</span>
                <span v-show="!maxBytesMessage" class="mr-1"
                    >Please reduce the quality of your {{ fileTypeLabel || "file" }} and try again.</span
                >
            </p>
        </div>

        <div v-show="sizeWarning">
            <p class="small text-secondary mb-3 mt-1">
                <span class="icon icon-sm icon-glyphicons-basic-triangle-alert text-warning"></span>
                This file is <strong>{{ fileSizeReadable }}</strong> in size, which is quite large for
                {{ fileTypeLabelArticle }} {{ fileTypeLabel }}.
                <br />
                For an optimal learner experience, {{ fileTypeLabel }}s should be no larger than
                {{ warningBytesReadable }}.
            </p>
        </div>
    </div>
</template>

<script>
import http from "@/axios.js"
import axios from "axios"

export default {
    name: "FileUpload",
    props: {
        value: {
            type: [Object, Array],
            required: false,
            default: null,
        },
        // Controls red border for upload areas indicating requirement, or some other error.
        error: {
            type: Boolean,
            required: false,
            default: false,
        },
        // The maximum allowable size of the uploaded file.
        maxBytes: {
            type: Number,
            required: false,
            default: 100000000,
        },
        maxBytesMessage: {
            type: String,
            required: false,
            default: "",
        },
        warningBytes: {
            type: Number,
            required: false,
            default: null,
        },
        fileTypeLabel: {
            type: String,
            required: false,
            default: null,
        },
        replaceable: {
            type: Boolean,
            required: false,
            default: true,
        },
        placeholder: {
            type: String,
            required: false,
            default: "Drop a new image here",
        },
        accept: {
            type: String,
            required: false,
            default: ".png|.jpg|.mp4|.mp3",
        },
        // Useful for large files. If set to true, files will be uploaded directly from the
        // user's browser to Google Cloud Storage, using a signed URL.
        directUpload: {
            type: Boolean,
            required: false,
            default: false,
        },
        // Controls the appearance of the checkmark after uploading
        showUploadStatus: {
            type: Boolean,
            required: false,
            default: true,
        },
        placeholderIconClass: {
            type: String,
            required: false,
            default: "icon-glyphicons-basic-plus",
        },
        size: {
            type: String,
            required: false,
            default: "md",
        },
    },
    data() {
        return {
            uploadedFile: null,
            fileSize: 0,
            isUploading: false,
            uploadTotal: 0,
            uploadProgressPercent: 0,
            isUploaded: false,
            inMemoryFileUrl: null,
        }
    },
    computed: {
        uploadProgressAsStyle() {
            return { width: this.uploadProgressPercent + "%" }
        },
        fileTypeLabelPlural() {
            return this.fileTypeLabel ? this.fileTypeLabel + "s" : null
        },
        fileTypeLabelArticle() {
            if (!this.fileTypeLabel) {
                return "a"
            }
            return this.fileTypeLabel.match("^[aieouAIEOU].*") ? "an" : "a"
        },
        sizeError() {
            return this.maxBytes && this.fileSize > this.maxBytes
        },
        sizeWarning() {
            return !this.sizeError && this.warningBytes && this.fileSize > this.warningBytes
        },
        fileSizeReadable() {
            return this.displayBytesReadable(this.fileSize)
        },
        maxBytesReadable() {
            return this.displayBytesReadable(this.maxBytes, true)
        },
        warningBytesReadable() {
            if (this.warningBytes) {
                return this.displayBytesReadable(this.warningBytes, true)
            }
            return ""
        },
    },
    watch: {
        uploadedFile() {
            this.$emit("input", this.uploadedFile)
        },
        value() {
            this.uploadedFile = this.value
            if (!this.isUploading) {
                this.inMemoryFileUrl = null
            }
        },
    },
    methods: {
        // Add a new file when dropped into the dropzone.
        async addFile($event) {
            let reader = new FileReader()
            let vm = this
            let file = $event.target.files[0]
            this.fileSize = file.size

            if (this.maxBytes && file.size > this.maxBytes) {
                // Reject if the file is too large. Clear form input, so that change
                // event will always fire.
                this.clearInputs()
                return
            }

            // Callback to render image thumbnails.
            reader.onload = function (e) {
                // Only do this for image file types.
                if (file.type.startsWith("image")) {
                    let newFile = {
                        content_type: file.type,
                        name: file.name,
                    }
                    // Set the file's URL to the in-memory file.
                    newFile.signed_url = e.target.result
                    vm.uploadedFile = newFile
                } else if (!this.directUpload) {
                    let newFile = {
                        content_type: file.type,
                        name: file.name,
                        file: file,
                    }
                    vm.uploadedFile = newFile
                }
            }

            // Read file data.
            reader.readAsDataURL($event.target.files[0])

            if (this.directUpload) {
                this.isUploading = true
                this.$emit("upload-started")
                // Fetch an upload URL and stored file from the server.
                let payload = {
                    name: file.name,
                    content_type: file.type,
                }
                let response = await http.post("/api/files/get-upload-url/", payload)
                let uploadUrl = response.data.url
                let storedFile = response.data.stored_file
                let signedUrl = storedFile.signed_url
                this.$emit("file-id-created", storedFile.id)

                // Only set the in-memory url of the dropped file if the file
                // is an image. For other file types like videos, this src string can be
                // hugely long.
                if (storedFile.content_type.startsWith("image")) {
                    this.inMemoryFileUrl = this.uploadedFile.signed_url
                }

                // Update the file object so that the input event fires and the
                // parent can see the new file object.
                this.uploadedFile = {
                    content_type: storedFile.content_type,
                    id: storedFile.id,
                    name: storedFile.name,
                    size_bytes: storedFile.size_bytes,
                    access_key: storedFile.access_key,
                    isNewlyUploaded: true,
                }

                // Construct the form data.
                let formData = new FormData()
                formData.append("file", file)

                // Upload the file - don't use the normal http object, because we don't want
                // to send CSRF tokens.
                let vm = this
                await axios({
                    method: "PUT",
                    data: file,
                    url: uploadUrl,
                    onUploadProgress: function (progressEvent) {
                        vm.uploadProgressPercent = (progressEvent.loaded * 100) / progressEvent.total
                    },
                })
                    .then(() => {
                        vm.$emit("upload-complete")
                        vm.isUploaded = true
                        vm.uploadedFile.signed_url = signedUrl
                    })
                    .catch(() => {
                        vm.$notify({
                            group: "main",
                            type: "danger",
                            text: "Upload failed. Please try again.",
                            ignoreDuplicates: true,
                            duration: 6000,
                            data: {},
                        })
                        vm.$emit("upload-failed")
                    })
                    .finally(() => {
                        vm.$emit("upload-ended")
                        vm.isUploading = false
                        vm.uploadProgressPercent = 0
                    })
            }
            // Clear the form input, so that change event will always fire.
            this.clearInputs()
        },
        // Remove an uploaded file.
        removeFile() {
            this.isUploaded = false
            this.uploadedFile = null
        },
        // Clear form inputs.
        clearInputs() {
            let uploadMulti = document.getElementById("upload-multi")
            let uploadSingle = document.getElementById("upload-single")
            if (uploadMulti) {
                uploadMulti.value = null
            }
            if (uploadSingle) {
                uploadSingle.value = null
            }
        },
        displayBytesReadable(sizeBytes, rounded = false) {
            if (sizeBytes < 1000) {
                return sizeBytes + "bytes"
            } else if (sizeBytes < 1000 * 1000) {
                var kilobytes = (sizeBytes / 1000).toFixed(0)
                return kilobytes + "KB"
            } else {
                var megabytes = (sizeBytes / (1000 * 1000)).toFixed(rounded ? 0 : 2)
                return megabytes + "MB"
            }
        },
        reset() {
            this.isUploaded = false
        },
    },
    mounted() {
        this.uploadedFile = this.value
        if (this.uploadedFile && this.warningBytes && this.uploadedFile.size_bytes > this.warningBytes) {
            // Accept the input, but show the warning.
            this.fileSize = this.uploadedFile.size_bytes
        }
    },
}
</script>

<style scoped lang="scss">
@use "sass:math";
@import "@/assets/styles/variables.scss";

$button-width: 25px;
$preview-width: 320px;
$preview-width-sm: 275px;

.file-upload-container {
    width: 100%;
    border: dashed $secondary 1.5px;
    height: $preview-width;
}

.file-upload-container-sm {
    height: $preview-width-sm !important;
}

.file-upload-error {
    border-color: $danger !important;
}

.file-upload-input {
    opacity: 0;
    width: 100%;
    height: 100%;
}

.card {
    max-width: 100%;
    height: $preview-width;
    width: $preview-width;
}

.card-sm {
    height: $preview-width-sm !important;
    width: $preview-width-sm !important;
}

.upload-progress {
    width: $preview-width;
}

.upload-progress-sm {
    width: $preview-width-sm !important;
}

.remove-file {
    color: $gray-550;
    text-align: center;
    height: $button-width;
    width: $button-width;
    top: math.div(-$button-width, 2);
    right: math.div(-$button-width, 2);
    cursor: pointer;
    align-items: center;
    display: flex;
    position: absolute;
    span {
        padding: 0.1rem;
        border-radius: $button-width;
        margin-bottom: 2px;
        margin-left: 1px;
        background-color: $gray-500;
        color: #ffffff;
        &:hover {
            background-color: $gray-600;
        }
        transition: $btn-transition;
    }
}

.cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
    overflow: hidden;
}

.text-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    padding-left: 10px;
    padding-right: 10px;
}

.upload-overlay {
    pointer-events: none;
}
</style>
